import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { verifyAuthToken } from '../services/LoginServices';
import {
  AUTH_COOKIE_KEYS,
  getResolvedAuthToken,
  invalidateAuthSession,
  isAuthSessionValid,
  isAuthTokenVerified,
  markAuthTokenVerified,
  redirectUnauthenticatedDirectOpen,
} from '../utils/authSession';

function resolveInitialAuthStatus(token) {
  if (!isAuthSessionValid(token)) {
    return 'denied';
  }
  if (isAuthTokenVerified(token)) {
    return 'allowed';
  }
  return 'checking';
}

export default function RequireAuth() {
  const [cookies, , removeCookie] = useCookies(AUTH_COOKIE_KEYS);
  const location = useLocation();
  const authToken = getResolvedAuthToken(cookies);
  const sessionValid = isAuthSessionValid(authToken);
  const [authStatus, setAuthStatus] = useState(() => resolveInitialAuthStatus(authToken));

  useEffect(() => {
    let cancelled = false;
    const resolvedToken = getResolvedAuthToken(cookies);

    const validateSession = async () => {
      if (!isAuthSessionValid(resolvedToken)) {
        invalidateAuthSession(removeCookie);
        if (!cancelled) {
          setAuthStatus('denied');
        }
        redirectUnauthenticatedDirectOpen(removeCookie);
        return;
      }

      if (isAuthTokenVerified(resolvedToken)) {
        if (!cancelled) {
          setAuthStatus('allowed');
        }
        return;
      }

      const isValidToken = await verifyAuthToken({ myToken: resolvedToken });
      if (cancelled) {
        return;
      }

      if (!isValidToken || !isAuthSessionValid(resolvedToken)) {
        invalidateAuthSession(removeCookie);
        setAuthStatus('denied');
        redirectUnauthenticatedDirectOpen(removeCookie);
        return;
      }

      markAuthTokenVerified(resolvedToken);
      setAuthStatus('allowed');
    };

    setAuthStatus(resolveInitialAuthStatus(resolvedToken));
    validateSession();

    return () => {
      cancelled = true;
    };
  }, [cookies.myToken, location.pathname, removeCookie]);

  if (!sessionValid || authStatus !== 'allowed') {
    if (!sessionValid) {
      redirectUnauthenticatedDirectOpen(removeCookie);
    }
    return null;
  }

  return <Outlet />;
}
